/**
 * @deprecated This page is outdated. Use IntegrationsPage instead.
 * Will be removed after Google Drive OAuth is migrated to IntegrationsPage.
 * See: sessions/tasks/h-implement-email-ingestion/08-frontend-integration.md
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from '../components/ConfirmModal';
import { taggedFetch } from '../services/taggedFetch';
import './ConnectionsPage.css';

interface Integration {
    name: string;
    icon: string;
    enabled: boolean;
}

interface Connection {
    id: string;
    category: string;
    provider: string;
    email: string;
    status: 'active' | 'revoked' | 'expired';
    created_at: string;
    last_refreshed_at: string | null;
}

interface IntegrationCategory {
    title: string;
    integrations: Integration[];
}

const ConnectionsPage: React.FC = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [connections, setConnections] = useState<Connection[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [revokeModalOpen, setRevokeModalOpen] = useState<boolean>(false);
    const [connectionToRevoke, setConnectionToRevoke] = useState<string | null>(null);

    const categories: IntegrationCategory[] = [
        {
            title: t('connections.categories.fileStorage'),
            integrations: [
                { name: 'Google Drive', icon: '📁', enabled: true },
                { name: 'MS OneDrive', icon: '☁️', enabled: false },
                { name: 'Dropbox', icon: '📦', enabled: false },
                { name: 'Nextcloud', icon: '🔒', enabled: false },
            ],
        },
        {
            title: t('connections.categories.email'),
            integrations: [
                { name: 'GMail', icon: '📬', enabled: true },
                { name: 'Outlook / Office365', icon: '📧', enabled: false },
                { name: 'IMAP', icon: '📨', enabled: false },
            ],
        },
        {
            title: t('connections.categories.government'),
            integrations: [
                { name: 'ELAN-NRW', icon: '🏛️', enabled: false },
                { name: 'HI-Tier Online', icon: '🐄', enabled: false },
                { name: 'Wirtschaftsdünger', icon: '🌾', enabled: false },
            ],
        },
        {
            title: t('connections.categories.finance'),
            integrations: [
                { name: 'DATEV', icon: '💼', enabled: false },
                { name: 'ELSTER', icon: '📊', enabled: false },
            ],
        },
        {
            title: t('connections.categories.energy'),
            integrations: [
                { name: 'Marktstammdatenregister', icon: '⚡', enabled: false },
                { name: 'Westnetz', icon: '🔌', enabled: false },
            ],
        },
        {
            title: t('connections.categories.others'),
            integrations: [
                { name: 'agrarOffice', icon: '🖥️', enabled: false },
                { name: '365FarmNet', icon: '🌐', enabled: false },
                { name: 'Next Farming', icon: '📱', enabled: false },
                { name: 'Agris', icon: '🚜', enabled: false },
            ],
        },
    ];

    useEffect(() => {
        fetchConnections();
    }, []);

    const fetchConnections = async () => {
        try {
            setLoading(true);
            const response = await taggedFetch('/api/oauth/connections', {
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                const mappedData = data.map((conn: any) => {
                    // Map provider to category
                    let category = '';
                    if (conn.provider === 'google' || conn.provider === 'google-drive') {
                        category = t('connections.categories.fileStorage');
                    } else if (conn.provider === 'gmail') {
                        category = t('connections.categories.email');
                    } else if (conn.provider === 'microsoft') {
                        category = t('connections.categories.email');
                    } else {
                        category = t('connections.categories.others');
                    }

                    // Map provider name for display
                    let providerName = conn.provider;
                    if (conn.provider === 'google-drive') {
                        providerName = 'Google Drive';
                    }

                    return {
                        id: conn.id,
                        category: category,
                        provider: providerName,
                        email: conn.provider_user_id || 'Connected',
                        status: conn.connection_status,
                        created_at: conn.created_at,
                        last_refreshed_at: conn.last_refreshed_at,
                    };
                });
                setConnections(mappedData);
            } else if (response.status === 401) {
                setConnections([]);
            }
        } catch (err) {
            console.error('Error fetching connections:', err);
        } finally {
            setLoading(false);
        }
    };

    const isIntegrationConnected = (integrationName: string, categoryTitle: string): boolean => {
        return connections.some(
            conn => conn.provider === integrationName && conn.category === categoryTitle && conn.status === 'active'
        );
    };

    const handleIntegrationClick = async (integrationName: string, enabled: boolean, categoryTitle: string) => {
        if (!enabled) {
            showToast(t('connections.comingSoon', { integration: integrationName }), 'info');
            return;
        }

        // Check if already connected
        if (isIntegrationConnected(integrationName, categoryTitle)) {
            showToast(t('connections.alreadyConnected', { integration: integrationName }), 'info');
            return;
        }

        // Handle enabled integrations with real OAuth flow
        if (integrationName === 'Google Drive') {
            try {
                // Start OAuth authorization flow
                const response = await taggedFetch('/api/oauth/authorize', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        provider: 'google-drive'
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorDetail = errorData.detail || response.statusText;
                    throw new Error(errorDetail);
                }

                const data = await response.json();

                // Redirect to Google OAuth consent screen
                window.location.href = data.authorization_url;

            } catch (error: any) {
                console.error('Failed to start OAuth flow:', error);
                const errorMessage = error?.message || 'Unknown error';
                showToast(`${t('errors.OAUTH_AUTHORIZATION_FAILED')} (${errorMessage})`, 'error', 8000);
            }
        } else if (integrationName === 'GMail') {
            // Mock implementation for now
            const newConnection: Connection = {
                id: `mock-${Date.now()}`,
                category: categoryTitle,
                provider: 'GMail',
                email: 'a.subkhangulov@gmail.com',
                status: 'active',
                created_at: new Date().toISOString(),
                last_refreshed_at: null,
            };
            setConnections([...connections, newConnection]);
        } else {
            // Future: Launch integration wizard
            console.log(`Starting wizard for ${integrationName}`);
        }
    };

    const handleDisconnect = (connectionId: string) => {
        setConnectionToRevoke(connectionId);
        setRevokeModalOpen(true);
    };

    const confirmRevoke = async () => {
        if (!connectionToRevoke) return;

        setRevokeModalOpen(false);
        const connectionId = connectionToRevoke;
        setConnectionToRevoke(null);

        // For mock connections (starting with 'mock-'), just remove from state
        if (connectionId.startsWith('mock-')) {
            setConnections(connections.filter(c => c.id !== connectionId));
            return;
        }

        // For real connections, call the API
        const connection = connections.find(c => c.id === connectionId);
        if (!connection) return;

        // Map display name back to provider ID
        let providerId = connection.provider.toLowerCase().replace(' ', '-');
        if (connection.provider === 'Google Drive') {
            providerId = 'google-drive';
        }

        try {
            const response = await taggedFetch(`/api/oauth/revoke/${providerId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.ok) {
                // Refresh connections from server
                await fetchConnections();
                showToast(t('toast.oauthRevoked'), 'success');
            } else {
                throw new Error(`Revoke failed: ${response.statusText}`);
            }
        } catch (err: any) {
            console.error('Error revoking connection:', err);
            const errorMessage = err?.message || 'Unknown error';
            showToast(`${t('errors.OAUTH_REVOKE_FAILED')} (${errorMessage})`, 'error', 8000);
        }
    };

    const cancelRevoke = () => {
        setRevokeModalOpen(false);
        setConnectionToRevoke(null);
    };

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'active':
                return '#10b981';
            case 'revoked':
                return '#ef4444';
            case 'expired':
                return '#f59e0b';
            default:
                return '#6b7280';
        }
    };

    return (
        <div className="connections-page">
            <div className="container">
                <h1>{t('connections.title')}</h1>
                <p className="subtitle">{t('connections.subtitle')}</p>

                {categories.map((category, index) => (
                    <section key={index} className="integration-category">
                        <h2>{category.title}</h2>

                        <div className="integration-tiles">
                            {category.integrations.map((integration, idx) => {
                                const isConnected = isIntegrationConnected(integration.name, category.title);
                                const isDisabled = !integration.enabled || isConnected;

                                return (
                                    <button
                                        key={idx}
                                        className={`integration-tile ${isDisabled ? 'disabled' : ''}`}
                                        onClick={() => handleIntegrationClick(integration.name, integration.enabled, category.title)}
                                        disabled={isDisabled}
                                    >
                                    <svg className="add-indicator" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 2L11.5 7.5L17 9L11.5 10.5L10 16L8.5 10.5L3 9L8.5 7.5L10 2Z" fill="currentColor"/>
                                        <path d="M15 3L15.5 4.5L17 5L15.5 5.5L15 7L14.5 5.5L13 5L14.5 4.5L15 3Z" fill="currentColor"/>
                                    </svg>
                                        <span className="integration-icon">{integration.icon}</span>
                                        <span className="integration-name">{integration.name}</span>
                                        {!integration.enabled && (
                                            <span className="coming-soon-badge">{t('connections.soon')}</span>
                                        )}
                                        {integration.enabled && isConnected && (
                                            <span className="connected-badge">{t('connections.connected')}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Show active connections for this category */}
                        {connections.filter(conn => conn.category === category.title).length > 0 && (
                            <div className="active-connections">
                                {connections
                                    .filter(conn => conn.category === category.title)
                                    .map(conn => (
                                        <div key={conn.id} className="connection-card">
                                            <div className="connection-header">
                                                <span className="provider-name">{conn.provider}</span>
                                                <span
                                                    className="connection-status"
                                                    style={{ color: getStatusColor(conn.status) }}
                                                >
                                                    ● {t(`connections.status.${conn.status}`)}
                                                </span>
                                            </div>
                                            <div className="connection-details">
                                                <small className="connection-email">{conn.email}</small>
                                                <small>
                                                    {t('connections.connected')}: {new Date(conn.created_at).toLocaleDateString()}
                                                </small>
                                            </div>
                                            {conn.status === 'active' && (
                                                <button
                                                    className="revoke-button"
                                                    onClick={() => handleDisconnect(conn.id)}
                                                >
                                                    {t('connections.revoke')}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </section>
                ))}

                {loading && <p className="loading">{t('common.loading')}</p>}
            </div>

            <ConfirmModal
                isOpen={revokeModalOpen}
                title={t('connections.revokeTitle')}
                message={t('connections.confirmRevoke')}
                confirmText={t('connections.revoke')}
                onConfirm={confirmRevoke}
                onCancel={cancelRevoke}
                variant="danger"
            />
        </div>
    );
};

export default ConnectionsPage;
